import { Link } from "react-router-dom";
import { Heart, Check, ArrowRight, BookOpen, GraduationCap, Phone, Mail, Camera } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import DonateDialog from "@/components/DonateDialog";
import EnrollDialog from "@/components/EnrollDialog";
import { IMG, PROGRAMS } from "@/lib/content";

export default function Programs() {
  return (
    <>
      <PageHero
        testid="programs-hero"
        kicker="Our Programs"
        title="Five ways we're changing lives — and how you can help"
        subtitle="Mentorship, outreach, family sponsorship, early learning, and a foster care home being restored. Every program is an open door to partner."
        image={IMG.outreachBags}
      />

      {/* quick nav */}
      <section className="bg-white border-b border-plum/10 sticky top-[68px] z-40" data-testid="programs-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-6 overflow-x-auto py-4 no-scrollbar">
          {PROGRAMS.map((p) => (
            <a key={p.slug} href={`#${p.slug}`} className="whitespace-nowrap text-sm font-medium text-stone hover:text-plum transition-colors" data-testid={`programs-nav-${p.slug}`}>{p.name}</a>
          ))}
        </div>
      </section>

      {PROGRAMS.map((p, idx) => (
        <section key={p.slug} id={p.slug} className={`py-20 sm:py-28 relative z-10 scroll-mt-32 ${idx % 2 ? "bg-white" : ""}`} data-testid={`program-${p.slug}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className={`grid lg:grid-cols-2 gap-14 items-center ${idx % 2 ? "" : "lg:[&>*:first-child]:order-2"}`}>
              {/* gallery */}
              <Reveal>
                <div className="grid grid-cols-2 gap-4">
                  <div className="img-zoom rounded-2xl shadow-xl col-span-2 h-72">
                    <img src={p.gallery[0]} alt={p.name} className="w-full h-full object-cover rounded-2xl" />
                  </div>
                  {p.gallery.slice(1, 3).map((g, i) => (
                    <div key={i} className="img-zoom rounded-2xl shadow-md h-40">
                      <img src={g} alt={`${p.name} ${i + 2}`} className="w-full h-full object-cover rounded-2xl" />
                    </div>
                  ))}
                </div>
              </Reveal>

              {/* content */}
              <div>
                <span className="text-gold-dark text-sm tracking-[0.2em] uppercase font-semibold">{p.kicker}</span>
                <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-plum mt-3 leading-tight">{p.name}</h2>
                <div className="mt-6 space-y-4">
                  {p.body.map((para, i) => <p key={i} className="text-stone leading-relaxed">{para}</p>)}
                </div>

                <div className="mt-7 bg-cream border border-plum/10 rounded-2xl p-6">
                  <p className="text-plum font-semibold mb-3 text-sm uppercase tracking-wide">How you can help</p>
                  <ul className="space-y-2.5">
                    {p.asks.map((a) => (
                      <li key={a} className="flex items-start gap-3 text-stone text-sm"><Check className="text-gold-dark shrink-0 mt-0.5" size={17} />{a}</li>
                    ))}
                  </ul>
                </div>

                {p.contact && (
                  <div className="mt-4 flex flex-wrap gap-5 text-sm text-plum">
                    <a href={`tel:${p.contact.phone}`} className="flex items-center gap-2 hover:text-gold-dark"><Phone size={16} />{p.contact.phone}</a>
                    <a href={`mailto:${p.contact.email}`} className="flex items-center gap-2 hover:text-gold-dark break-all"><Mail size={16} />{p.contact.email}</a>
                  </div>
                )}

                <div className="flex flex-wrap gap-4 mt-7">
                  {p.enroll ? (
                    <EnrollDialog trigger={
                      <button data-testid={`enroll-btn-${p.slug}`} className="bg-plum hover:bg-plum-dark text-cream font-semibold rounded-full px-7 py-3.5 flex items-center gap-2 transition-all hover:-translate-y-1"><BookOpen size={17} /> Enroll Now</button>
                    } />
                  ) : (
                    <DonateDialog defaultDesignation={p.name === "Foster Care Home" ? "Foster Care Home" : p.name} trigger={
                      <button data-testid={`donate-btn-${p.slug}`} className="bg-gold hover:bg-gold-dark text-plum font-semibold rounded-full px-7 py-3.5 flex items-center gap-2 transition-all hover:-translate-y-1"><Heart size={17} /> Support This</button>
                    } />
                  )}
                  <Link to="/get-involved" className="border border-plum/25 text-plum rounded-full px-7 py-3.5 hover:bg-plum hover:text-cream transition-colors flex items-center gap-2" data-testid={`involved-btn-${p.slug}`}>
                    {p.enroll ? "Join Prayer Team" : "Volunteer / Give Goods"} <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            </div>

            {/* extra gallery for programs with many photos */}
            {p.gallery.length > 3 && (
              <Stagger className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                {p.gallery.slice(3).map((g, i) => (
                  <StaggerItem key={i}>
                    <div className="img-zoom rounded-xl h-32 shadow-sm"><img src={g} alt={`${p.name} gallery ${i}`} className="w-full h-full object-cover rounded-xl" /></div>
                  </StaggerItem>
                ))}
              </Stagger>
            )}

            {/* Photo Stories */}
            {p.photoStories && (
              <div className="mt-16" data-testid={`photo-stories-${p.slug}`}>
                <Reveal>
                  <div className="flex items-center gap-3 mb-2">
                    <Camera className="text-gold-dark" size={22} />
                    <h3 className="font-heading text-2xl sm:text-3xl text-plum font-semibold">{p.photoStories.title}</h3>
                  </div>
                  <p className="text-stone max-w-2xl">{p.photoStories.caption}</p>
                </Reveal>
                <Stagger className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  {p.photoStories.images.map((g, i) => (
                    <StaggerItem key={i}>
                      <div className={`img-zoom rounded-2xl shadow-md ${i === 0 ? "col-span-2 md:col-span-1 h-64 md:h-72 lg:row-span-2 lg:h-full" : "h-52 lg:h-56"}`}>
                        <img src={g} alt={`${p.photoStories.title} ${i + 1}`} className="w-full h-full object-cover rounded-2xl" />
                      </div>
                    </StaggerItem>
                  ))}
                </Stagger>
              </div>
            )}
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="py-24 bg-plum relative z-10" data-testid="programs-cta">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <GraduationCap className="text-gold mx-auto mb-5" size={40} />
          <h2 className="font-heading text-3xl sm:text-4xl text-cream font-semibold">Your generosity writes the next chapter</h2>
          <p className="text-cream/80 mt-5 text-lg">Whether it's a dollar, an hour, or a donated vehicle &mdash; you make these programs possible.</p>
          <div className="flex flex-wrap gap-4 justify-center mt-8">
            <DonateDialog trigger={<button className="bg-gold hover:bg-gold-dark text-plum font-semibold rounded-full px-8 py-4 flex items-center gap-2 transition-all hover:-translate-y-1" data-testid="programs-cta-donate"><Heart size={18} /> Donate Now</button>} />
            <Link to="/get-involved" className="border border-cream/40 text-cream rounded-full px-8 py-4 hover:bg-cream/10 transition-colors" data-testid="programs-cta-involved">Get Involved</Link>
          </div>
        </div>
      </section>
    </>
  );
}
